import { cn } from "@/lib/utils";
import { Columns3, Frame, Rows3 } from "lucide-react";
import { TooltipWrapper } from "../common/tooltip";
import { AxisIcon, LineItemIcon } from "./icons";

import { forwardRef } from "react";

const toolTipText = [
  "여러 라인아이템을 하나로 묶어주는 [상위 개요]",
  "라인아이템의 주제를 의미하는 [항목]",
  "주황색 [구성요소]는 합계열을 의미합니다.",
  "[라인아이템]을 드래그하면 행 순서를 조정할 수 있습니다.",
  "[구성요소]을 드래그하면 열 순서를 조정할 수 있습니다.",
  "[축]끼리의 순서를 바꾸면 표의 모양을 조정할 수 있습니다.",
  "어떤 라인아이템을 선택했는가에 따라 [개요]가 자동으로 설정됩니다.",
  "주황색 [라인아이템]은 합계를 의미합니다.",
];

const Icon = forwardRef<
  HTMLButtonElement,
  {
    className: string;
    children: React.ReactNode;
  }
>(({ className, children, ...props }, ref) => {
  return (
    <button
      type="button"
      ref={ref}
      className={cn(
        "w-8 h-8 rounded-[8px] flex items-center justify-center cursor-pointer",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});

Icon.displayName = "Icon";

type CustomTooltipProps = {
  label: string | React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  styles?: {
    contentStyle?: string;
    textStyle?: string;
    bgStyle?: string;
    arrowStyle?: string;
  };
};

const CustomArrowedButtonTooltip = (props: CustomTooltipProps) => {
  return (
    <TooltipWrapper
      {...props}
      arrowed={true}
      styles={{
        contentStyle: "border-none p-0 items-center justify-center",
        textStyle: "caption-1-regular text-white text-center",
        bgStyle: "px-[16px] py-[12px] bg-black max-w-[180px] rounded-md",
        arrowStyle: "border-none",
        ...props.styles,
      }}
    />
  );
};

const Icons = () => {
  return (
    <div className="flex w-[191px] justify-center items-center content-center gap-[20px] flex-wrap">
      <CustomArrowedButtonTooltip label={toolTipText[0]}>
        <Icon className="bg-blue-500 hover:border-[1px] hover:border-blue-300 hover:shadow-[0px_0px_0px_2px_#DBEAFE] text-white">
          <Frame className="w-4 h-4 " />
        </Icon>
      </CustomArrowedButtonTooltip>
      <CustomArrowedButtonTooltip label={toolTipText[1]}>
        <Icon className="bg-slate-200 text-black hover:border-[1px] hover:border-slate-300 hover:shadow-[0px_0px_0px_2px_#F1F5F9]">
          <Rows3 className="w-4 h-4 " />
        </Icon>
      </CustomArrowedButtonTooltip>
      <CustomArrowedButtonTooltip label={toolTipText[2]}>
        <Icon className="bg-orange-100 text-orange-600 border border-orange-300 hover:border-[1px] hover:border-orange-300 hover:shadow-[0px_0px_0px_2px_#FFEDD5]">
          <AxisIcon className="w-4 h-4 " isExtendedTag={false} />
        </Icon>
      </CustomArrowedButtonTooltip>
      <CustomArrowedButtonTooltip label={toolTipText[3]}>
        <Icon className="bg-blue-100 text-black hover:border-[1px] hover:border-blue-300 hover:shadow-[0px_0px_0px_2px_#DBEAFE]">
          <LineItemIcon className="w-4 h-4 " isExtendedTag={false} />
        </Icon>
      </CustomArrowedButtonTooltip>
      <CustomArrowedButtonTooltip label={toolTipText[4]} side="bottom">
        <Icon className="bg-blue-100 text-black hover:border-[1px] hover:border-blue-300 hover:shadow-[0px_0px_0px_2px_#DBEAFE]">
          <AxisIcon className="w-4 h-4 " isExtendedTag={false} />
        </Icon>
      </CustomArrowedButtonTooltip>
      <CustomArrowedButtonTooltip label={toolTipText[5]} side="bottom">
        <Icon className="bg-slate-200 text-black hover:border-[1px] hover:border-slate-300 hover:shadow-[0px_0px_0px_2px_#F1F5F9]">
          <Columns3 className="w-4 h-4 " />
        </Icon>
      </CustomArrowedButtonTooltip>
      <CustomArrowedButtonTooltip label={toolTipText[6]} side="bottom">
        <Icon className="bg-slate-200 text-black hover:border-[1px] hover:border-slate-300 hover:shadow-[0px_0px_0px_2px_#F1F5F9]">
          <Frame className="w-4 h-4 " />
        </Icon>
      </CustomArrowedButtonTooltip>
      <CustomArrowedButtonTooltip label={toolTipText[7]} side="bottom">
        <Icon className="bg-orange-100 text-orange-600 border border-orange-300 hover:border-[1px] hover:border-orange-300 hover:shadow-[0px_0px_0px_2px_#FFEDD5]">
          <LineItemIcon className="w-4 h-4 " isExtendedTag={false} />
        </Icon>
      </CustomArrowedButtonTooltip>
    </div>
  );
};

const EmptyMessage = () => {
  return (
    <div className="text-center w-[316px] h-[44px]">
      <span className="text-gray-600 body-1-normal-medium">
        선택한 태그가 없어, 현재 보여줄 내용이 없습니다. 태깅 탭에서 태그를
        선택해 주세요.
      </span>
    </div>
  );
};

export const PreviewEmptyView = () => {
  return (
    <div className="flex flex-col items-center justify-center h-full space-y-6">
      <Icons />
      <EmptyMessage />
    </div>
  );
};
